import { Table } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { PHONE_MAX_WIDTH } from "@/shared/theme/breakpoints";
import { CustomHeaderCell } from "./components/CustomHeaderCell";
import { useProcessedData } from "./hooks/useProcessedData";
import { useTableFilters } from "./hooks/useTableFilters";
import { useTableSort } from "./hooks/useTableSort";
import { CustomProfessionalTableGlobalStyle, HeaderCellWrapper, HeaderTitle } from "./styles";
import type { CustomTableProps, LooseColumn } from "./types";
import { getLooseEllipsis } from "./utils/columnUtils";
import { getColumnBehavior, getSortLabels } from "./utils/tableHelpers";

/** Width assumed for a column that doesn't declare a numeric width, when fitting columns. */
const DEFAULT_COLUMN_WIDTH = 120;

export function CustomTable<T extends object>(props: CustomTableProps<T>) {
	const {
		columns,
		filterState: externalFilterState,
		onFilterStateChange,
		className,
		tableLayout,
		dataSource,
		scroll,
		fitColumns = false,
		...antdTableProps
	} = props;

	const baseColumns = useMemo(() => columns || [], [columns]);

	// Phone mode: only the column marked `mobile: "primary"`, at full width. Filters and
	// sorting still run against every column.
	const isPhone = useMaxWidth(PHONE_MAX_WIDTH);
	const phoneMode = isPhone && baseColumns.some((col) => col.mobile === "primary");

	// fitColumns: measure the space the table has, so lower-priority columns can be dropped.
	const containerRef = useRef<HTMLDivElement>(null);
	const [availableWidth, setAvailableWidth] = useState<number | null>(null);
	useEffect(() => {
		const element = containerRef.current;
		if (!fitColumns || !element || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver(([entry]) => setAvailableWidth(entry.contentRect.width));
		observer.observe(element);
		return () => observer.disconnect();
	}, [fitColumns]);

	const displayColumns = useMemo(() => {
		if (phoneMode) {
			return baseColumns
				.filter((col) => col.mobile === "primary")
				.map((col) => ({ ...col, width: undefined, fixed: undefined }));
		}
		if (!fitColumns || availableWidth === null) return baseColumns;

		let used = 0;
		return baseColumns.filter((col) => {
			const width = typeof col.width === "number" ? col.width : DEFAULT_COLUMN_WIDTH;
			if (col.mobile !== "primary" && used + width > availableWidth) return false;
			used += width;
			return true;
		});
	}, [availableWidth, baseColumns, fitColumns, phoneMode]);

	const {
		filterState,
		openDropdown,
		setOpenDropdown,
		filterSearch,
		filterOptionsByColumn,
		toggleFilterValue,
		handleFilterSearchChange,
	} = useTableFilters<T>({
		baseColumns,
		dataSource: dataSource as T[] | undefined,
		externalFilterState,
		onFilterStateChange,
	});

	const { sortState, handleSortSelect } = useTableSort({
		onSortApplied: () => setOpenDropdown(null),
	});

	const processedData = useProcessedData<T>({
		baseColumns,
		dataSource: dataSource as T[] | undefined,
		filterState,
		sortState,
		searchTerm: "",
	});

	const enhancedColumns = useMemo(
		() =>
			displayColumns.map((col) => {
				const looseCol = col as LooseColumn<T>;
				const colKey = looseCol.key ?? looseCol.dataIndex;
				const isActionColumn = typeof col.title === "string" && col.title.toLowerCase() === "action";

				if (typeof col.title !== "string" || isActionColumn) {
					return {
						...col,
						title: (
							<HeaderCellWrapper>
								<HeaderTitle>
									{typeof col.title === "string" ? col.title : String(colKey ?? "")}
								</HeaderTitle>
							</HeaderCellWrapper>
						),
					};
				}

				const columnKey = String(colKey || "");
				const { enableSort, enableFilter, sortLabelType } = getColumnBehavior(col);

				return {
					...col,
					sorter: false,
					ellipsis: getLooseEllipsis(looseCol) ?? true,
					showSorterTooltip: false,
					title: () => (
						<CustomHeaderCell
							title={col.title as string}
							columnKey={columnKey}
							enableSort={enableSort}
							enableFilter={enableFilter}
							sortState={sortState}
							openDropdown={openDropdown}
							setOpenDropdown={setOpenDropdown}
							filterOptions={filterOptionsByColumn[columnKey] || []}
							selectedFilters={filterState[columnKey] || []}
							filterSearch={filterSearch[columnKey] || ""}
							onSortSelect={handleSortSelect}
							onFilterSearchChange={handleFilterSearchChange}
							onFilterToggle={toggleFilterValue}
							sortLabels={getSortLabels(sortLabelType)}
						/>
					),
				};
			}),
		[
			displayColumns,
			filterOptionsByColumn,
			filterSearch,
			filterState,
			handleFilterSearchChange,
			handleSortSelect,
			openDropdown,
			setOpenDropdown,
			sortState,
			toggleFilterValue,
		],
	);

	const table = (
		<>
			<CustomProfessionalTableGlobalStyle />
			<Table
				{...antdTableProps}
				dataSource={processedData}
				columns={enhancedColumns}
				// A single full-width column, or columns fitted to the width, never need horizontal scroll.
				scroll={phoneMode || fitColumns ? { ...scroll, x: undefined } : scroll}
				tableLayout={tableLayout ?? "fixed"}
				className={`${className || ""} custom-professional-table`}
			/>
		</>
	);

	return fitColumns ? <div ref={containerRef}>{table}</div> : table;
}

export type { CustomTableColumn, CustomTableProps, TableFilterState } from "./types";

export { getLooseColumnKey } from "./utils/columnUtils";
